/**
 * Product Recommendations Section JavaScript
 * Handles Swiper initialization and product recommendations API fetching
 * CLS-optimized with progressive enhancement
 */

(function() {
  'use strict';

  class ProductRecommendations extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.productId = this.dataset.productId;
      this.url = this.dataset.url;
      this.layout = this.dataset.layout;
      this.swiperInstance = null;
      this.isInitialized = false;
      this.hasFetched = false;
      this.visibilityObserver = null;
    }

    connectedCallback() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.init());
      } else {
        this.init();
      }
    }

    init() {
      const hasRealProducts = this.querySelector('.product-recommendations__container .product-card:not(.product-card--placeholder)');
      const whenVisible = window.theme?.whenVisible || ((element, callback) => callback(element));
      
      if (!hasRealProducts && !this.hasFetched) {
        this.visibilityObserver = whenVisible(this, () => this.fetchRecommendations());
      } else if (hasRealProducts) {
        this.classList.add('product-recommendations--loaded');
        this.visibilityObserver = whenVisible(this, () => this.initializeLayout());
      }
    }

    async fetchRecommendations() {
      if (this.hasFetched) return;
      this.hasFetched = true;
      
      try {
        const response = await fetch(this.url);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const text = await response.text();
        const html = new DOMParser().parseFromString(text, 'text/html');
        const recommendationsContainer = html.querySelector('product-recommendations');
        
        if (recommendationsContainer) {
          const newContent = recommendationsContainer.querySelector('.product-recommendations__container');
          
          if (newContent && newContent.innerHTML.trim()) {
            const hasProducts = newContent.querySelector('.product-recommendations__grid, .product-recommendations__slider-container');
            
            if (hasProducts) {
              const currentContainer = this.querySelector('.product-recommendations__container');
              if (currentContainer) {
                currentContainer.innerHTML = newContent.innerHTML;
                this.classList.add('product-recommendations--loaded');
                
                requestAnimationFrame(() => {
                  this.initializeLayout();
                });
              }
            } else {
              this.style.display = 'none';
            }
          } else {
            this.style.display = 'none';
          }
        } else {
          this.style.display = 'none';
        }
      } catch (error) {
        console.error('Error fetching product recommendations:', error);
        this.style.display = 'none';
      }
    }

    initializeLayout() {
      if (this.layout === 'slider' && !this.isInitialized) {
        this.waitForSwiper();
      }
    }

    waitForSwiper() {
      window.loadThemeSwiper().then(() => {
        this.initSlider();
      }).catch((error) => {
        console.warn('Swiper failed to load for ProductRecommendations:', error);
      });
    }

    initSlider() {
      const swiperElement = this.querySelector('.swiper');
      if (!swiperElement || this.isInitialized) return;

      const slides = swiperElement.querySelectorAll('.swiper-slide');
      const slideCount = slides.length;

      if (slideCount === 0) return;

      // Parse configuration
      let config = {};
      try {
        config = JSON.parse(swiperElement.dataset.swiperConfig || '{}');
      } catch (e) {
        console.warn('Invalid Swiper config:', e);
      }

      // Get slides per view for current breakpoint
      const getSlidesPerView = () => {
        const width = window.innerWidth;
        if (width >= 990 && config.breakpoints && config.breakpoints['990']) {
          return config.breakpoints['990'].slidesPerView || 4;
        } else if (width >= 750 && config.breakpoints && config.breakpoints['750']) {
          return config.breakpoints['750'].slidesPerView || 3;
        }
        return config.slidesPerView || 2;
      };

      const slidesPerView = getSlidesPerView();

      // Only loop if we have enough slides
      const shouldLoop = config.loop !== false && slideCount > slidesPerView;

      // Get navigation elements
      const prevButton = this.querySelector('.product-recommendations__nav--prev');
      const nextButton = this.querySelector('.product-recommendations__nav--next');
      const pagination = this.querySelector('.product-recommendations__pagination');

      // Build Swiper configuration
      const swiperConfig = {
        slidesPerView: config.slidesPerView || 2,
        spaceBetween: config.spaceBetween || 12,
        speed: config.speed || 300,
        loop: shouldLoop,
        loopFillGroupWithBlank: false,
        watchOverflow: true,
        grabCursor: true,
        keyboard: {
          enabled: true
        },
        breakpoints: config.breakpoints || {
          '750': {
            slidesPerView: 3,
            spaceBetween: 16
          },
          '990': {
            slidesPerView: 4,
            spaceBetween: 16
          }
        },
        on: {
          init: () => {
            this.isInitialized = true;
            swiperElement.classList.add('swiper-initialized');
          }
        }
      };

      // Add navigation
      if (prevButton && nextButton) {
        swiperConfig.navigation = {
          prevEl: prevButton,
          nextEl: nextButton,
          disabledClass: 'swiper-button-disabled'
        };
      }

      // Add pagination
      if (pagination) {
        swiperConfig.pagination = {
          el: pagination,
          clickable: true
        };
      }

      // Initialize Swiper
      try {
        this.swiperInstance = new Swiper(swiperElement, swiperConfig);
        
        // Force Swiper to recalculate dimensions and refresh
        requestAnimationFrame(() => {
          if (this.swiperInstance) {
            // Recalculate all dimensions
            this.swiperInstance.update();
            this.swiperInstance.updateSize();
            this.swiperInstance.updateSlides();
            this.swiperInstance.updateProgress();
            this.swiperInstance.updateSlidesClasses();
            this.swiperInstance.slideTo(0, 0, false);
            
            // Force slide visibility
            this.swiperInstance.slides.forEach(slide => {
              slide.style.visibility = 'visible';
              slide.style.opacity = '1';
            });
          }
        });

      } catch (error) {
        console.error('Error initializing Swiper:', error);
      }
    }

    disconnectedCallback() {
      this.visibilityObserver?.disconnect?.();
      if (this.swiperInstance) {
        this.swiperInstance.destroy(true, true);
        this.swiperInstance = null;
      }
    }
  }

  // Register custom element
  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', ProductRecommendations);
  }

  // Handle section reload in Theme Editor
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function(event) {
      const section = event.target.querySelector('product-recommendations');
      if (section && !section.isInitialized) {
        section.initializeLayout?.();
      }
    });

    document.addEventListener('shopify:section:unload', function(event) {
      const section = event.target.querySelector('product-recommendations');
      if (section && section.swiperInstance) {
        section.swiperInstance.destroy(true, true);
        section.swiperInstance = null;
        section.isInitialized = false;
      }
    });
  }

})();
